import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Menu, X, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../lib/AuthContext';

interface NavbarProps {
  isRecruiterSite?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ isRecruiterSite = true }) => {
  const { i18n } = useTranslation();
  const { session } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const portalPath = session ? '/app/dashboard' : '/login';
  const workerPortalPath = session ? '/worker/portal' : '/worker/login';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const langs = ['en', 'hinglish', 'hi'];
    const nextLang = langs[(langs.indexOf(i18n.language) + 1) % langs.length];
    i18n.changeLanguage(nextLang);
  };

  const langLabel =
    i18n.language === 'hinglish' ? 'HI/EN' :
    i18n.language === 'hi' ? 'हिंदी' :
    'EN';

  const links = isRecruiterSite
    ? [
      { label: 'Features', href: '#features' },
      { label: 'How it Works', href: '#how-it-works' },
      { label: 'Campaigns', href: '#posterwall' },
    ]
    : [
      { label: 'What We Do', href: '#what-we-do' },
      { label: 'How it Works', href: '#how-it-works' },
      { label: 'Why Jobisa', href: '#why-jobisa' },
    ];

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/85 backdrop-blur-2xl border-b border-black/[0.06] py-3'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between items-center">
        <Link to={isRecruiterSite ? '/recruiter' : '/'} className="group flex items-center select-none">
          <img
            src="/wordmark.png"
            alt="jobisa"
            className="h-12 w-auto transition-all duration-300"
          />
        </Link>

        <div className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="px-4 py-2 text-[14px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-all"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-slate-800 bg-slate-100/70 hover:bg-slate-100 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Globe size={13} />
            {langLabel}
          </button>

          {isRecruiterSite ? (
            <>
              <Link
                to={portalPath}
                className="text-[14px] font-medium text-slate-600 hover:text-slate-900 px-3 py-2 transition-colors"
              >
                {session ? 'Dashboard' : 'Sign in'}
              </Link>
              <Link
                to={portalPath}
                className="group relative overflow-hidden bg-slate-950 text-white text-[14px] font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 hover:bg-slate-800 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.3)]"
              >
                Get Started
                <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </>
          ) : (
            <>
              <Link
                to={workerPortalPath}
                className="text-[14px] font-semibold text-orange-700 hover:text-orange-800 border border-orange-200 hover:border-orange-300 bg-orange-50 hover:bg-orange-100 px-4 py-2 rounded-full transition-all"
              >
                {session ? 'Worker Jobs' : 'Worker Login'}
              </Link>
              <a
                href="/recruiter"
                className="text-[14px] font-semibold text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300 bg-blue-50/50 hover:bg-blue-50 px-4 py-2 rounded-full transition-all"
              >
                Agency Portal →
              </a>
            </>
          )}
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-700 transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden absolute top-full left-0 w-full bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-2xl py-6 px-6 flex flex-col gap-5"
          >
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-700 font-medium text-lg"
              >
                {link.label}
              </a>
            ))}
            <div className="border-t border-slate-100 pt-5 flex flex-col gap-3">
              <Link
                to={isRecruiterSite ? portalPath : workerPortalPath}
                className="w-full bg-slate-950 text-white text-center font-bold py-3.5 rounded-2xl shadow-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {isRecruiterSite ? (session ? 'Go to Dashboard' : 'Get Started Free') : (session ? 'Open Worker Jobs' : 'Worker Login')}
              </Link>
              <button
                onClick={toggleLanguage}
                className="flex items-center justify-center gap-2 text-slate-500 text-sm font-medium"
              >
                <Globe size={14} /> Language: {langLabel}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
